import { FC, useCallback, useMemo } from 'react'

import { Forms } from '@inno-ui/file-icons'
import {
  ArrowUpRectangleLineIcon,
  IconSizes,
  IconWeight,
  MultiplyIcon
} from '@inno-ui/icons'
import {
  ActivityToast,
  ActivityToastGroup,
  ActivityToastItem,
  ActivityToastItemStatus
} from '@inno-widgets/activity-toast'
import '@inno-widgets/activity-toast/lib/assets/index.css'
import { useTranslation } from 'react-i18next'

import { UploadStatus, useUploaderStore } from '../../store/uploadStore'
import styles from './FileUploadProgressToast.module.scss'

const statusesToShow: UploadStatus[] = ['pending', 'uploading', 'failed']

const convertStatusToToastStatus = (
  status: UploadStatus
): ActivityToastItemStatus => {
  switch (status) {
    case 'completed':
      return ActivityToastItemStatus.Completed
    case 'pending':
      return ActivityToastItemStatus.Pending
    case 'failed':
      return ActivityToastItemStatus.Failed
    case 'uploading':
      return ActivityToastItemStatus.InProgress
    default:
      return ActivityToastItemStatus.Pending
  }
}

export const FileUploadProgressToast: FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'desktop.file_upload'
  })

  const { items, cancelUpload, clearCompleted } = useUploaderStore()

  const handleCancelFile = useCallback(
    (id: string) => {
      cancelUpload(id)
    },
    [cancelUpload]
  )

  const handleCancelAll = useCallback(() => {
    items.forEach(item => {
      if (item.status === 'pending' || item.status === 'uploading') {
        cancelUpload(item.id)
      }
    })
    clearCompleted()
  }, [items, cancelUpload, clearCompleted])

  const activityToastGroups: ActivityToastGroup[] = useMemo(() => {
    const visibleItems = items.filter(item =>
      statusesToShow.includes(item.status)
    )

    if (visibleItems.length === 0) {
      return []
    }

    const groupItems: ActivityToastItem[] = visibleItems.map(item => {
      const toastStatus = convertStatusToToastStatus(item.status)
      const isFailed = toastStatus === ActivityToastItemStatus.Failed

      return {
        id: item.id,
        icon: isFailed
          ? () => (
              <MultiplyIcon
                size={IconSizes.Size34}
                color='var(--warning-red-light)'
                weight={IconWeight.Bold}
              />
            )
          : Forms,
        label: isFailed
          ? `${t('failed_label')} ${item.fileName}`
          : item.fileName,
        status: toastStatus,
        actions: [],
        progress: item.progress,
        onCancel: () => handleCancelFile(item.id)
      }
    })

    return [
      {
        id: 'desktop-uploads',
        title: t('uploading_title'),
        icon: ArrowUpRectangleLineIcon,
        items: groupItems,
        onClose: handleCancelAll
      }
    ]
  }, [items, t, handleCancelFile, handleCancelAll])

  if (activityToastGroups.length === 0) {
    return null
  }

  return (
    <ActivityToast
      className={styles.activityToast}
      groups={activityToastGroups}
    />
  )
}
